
import { LockClosedIcon, MapPinIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import SelectMe from 'react-select-me';

import {
  formatCurrency, formatDate, formatValue,
} from '../../../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import type { YearOption } from '../../../../../hooks/useDvfYears';
import { useDvfYears } from '../../../../../hooks/useDvfYears';
import type { RootState } from '../../../../../store/store';
import classNames from '../../../../../utils/classNames';
import { JWTAuthorization, JWTHeader } from '../../../../../utils/JWTHeader';
import { ROUTES } from '../../../../../utils/routes';
import { hasModuleAccess } from '../../../../../utils/subscriptions';
import tailwindConfig from '../../../../../utils/tailwindColors';
import Badge from '../../../../common/Badge';
import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup';
import RedirectionCoproButtons from '../../../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../../../common/RedirectionMapButtons';
import Table from '../../../../common/Table';

const dataFetchHeaders = ['immatriculation', 'name', 'address', 'cp', 'city', 'salesAmount', 'salesCommission', 'nbLots', 'nbSales', 'm2ForNbSales', 'salesAvgForM2'];

interface DvfSalesDetail {
  valeurfonc?: number | string;
  buildingSurface?: number | string;
  sbati?: number | string;
  nblot?: number | string;
  lots?: string;
  datemut?: string;
  libtypbien?: string;
  rooms?: number | string;
}

const finalHeaders = [
  'Immatriculation', 'Nom', 'Adresse', 'Code postal', 'Ville',
  'Montant total des ventes', 'Commission', 'Nb lots hab / local pro',
  'Nb ventes', 'm2 moyen / vente', 'Prix moyen / m2', 'Détails',
];

const sortableColumns = {
  Immatriculation: 'string',
  Nom: 'string',
  Adresse: 'string',
  'Code postal': 'string',
  Ville: 'string',
  'Nb lots hab / local pro': 'number',
  'Nb ventes': 'number',
  'm2 moyen / vente': 'number',
  'Prix moyen / m2': 'number',
  'Montant total des ventes': 'number',
  Commission: 'number',
};

const smartConcatMerge = (currentValue: React.ReactNode, valueToConcat: React.ReactNode): React.ReactNode => {
  if (valueToConcat && valueToConcat !== '') {
    if (currentValue !== '') {
      return (
        <>
          {currentValue}
          <div className="mx-auto w-3/4 border-b border-gray-400" />
          {valueToConcat}
        </>
      );
    }
    return valueToConcat;
  }
  return currentValue;
};

interface CoproAverageSalesProps {
  setSelectedLocationIdProp: (id: number) => void;
}

function CoproAverageSales({ setSelectedLocationIdProp }: CoproAverageSalesProps) {
  const [selectedYears, setSelectedYears] = useState<YearOption[]>([]);
  const [data, setData] = useState<Record<string, string | number | boolean | JSX.Element | undefined | object | unknown[]>[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [coproSalesDetails, setCoproSalesDetails] = useState<DvfSalesDetail[] | null>(null);
  const [selectedCoproAddress, setSelectedCoproAddress] = useState('');
  const { yearsOptions, getDefaultSelection } = useDvfYears({ startYear: 2014 });

  const kpis = useMemo(() => {
    if (!Array.isArray(coproSalesDetails) || coproSalesDetails.length === 0) {
      return {
        avgPricePerM2: 0,
        avgPricePerSale: 0,
        salesCount: 0,
        totalSalesAmount: 0,
        totalCommission: 0,
      };
    }

    let totalSalesAmount = 0;
    let totalSurface = 0;

    for (const item of coproSalesDetails) {
      const saleAmount = Number(item.valeurfonc) || 0;
      const surface = (Number(item.buildingSurface) || 0) || (Number(item.sbati) || 0);
      totalSalesAmount += saleAmount;
      if (surface > 0) {
        totalSurface += surface;
      }
    }

    const salesCount = coproSalesDetails.length;
    const avgPricePerM2 = totalSurface > 0 ? totalSalesAmount / totalSurface : 0;
    const avgPricePerSale = salesCount > 0 ? totalSalesAmount / salesCount : 0;
    const totalCommission = totalSalesAmount * 0.05;

    return { avgPricePerM2, avgPricePerSale, salesCount, totalSalesAmount, totalCommission };
  }, [coproSalesDetails]);

  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const currentFetchRef = useRef<AbortController | null>(null);

  const getCoproSalesDetails = async(id: number) => {
    const params = new URLSearchParams();
    const selectedYearsValues = selectedYears.map((year) => year.value);
    selectedYearsValues.forEach((year) => params.append('years[]', year));

    try {
      const response = await fetch(`/api/copros/${id}/stats/sales/details/years?${params.toString()}`, JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Error fetching data');
      }

      const detailsData = await response.json();
      const formattedData = detailsData.map((dvf_entry) => ({
        nblot: dvf_entry.nblot,
        lots: dvf_entry.lots,
        sbati: dvf_entry.sbati,
        datemut: dvf_entry.datemut,
        libtypbien: dvf_entry.libtypbien,
        valeurfonc: dvf_entry.valeurfonc,
        buildingSurface: dvf_entry.buildingSurface,
        rooms: dvf_entry.rooms,
      }));
      setCoproSalesDetails(formattedData);
    } catch (error) {
      console.error('Error fetching copro sales details :', error);
      setPopupOpen(false);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const handleDetails = (id: number, address: string) => {
    setPopupOpen(true);
    setSelectedCoproAddress(address);
    getCoproSalesDetails(id);
  };

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      setSelectedYears(getDefaultSelection(1));
    }
  }, [yearsOptions]);

  useEffect(() => {
    if (!company || !jwt || !company.id || yearsOptions.length === 0) {return;}

    if (selectedYears.length === 0) {
      setStatus('ERROR');
    }

    setStatus('PENDING');

    const selectedYearsValues = selectedYears.map((year) => year.value);

    if (!hasModuleAccess(company, 'monitoring')) {
        const processResponseData = (coproAmount: number) => {
        const getRandomName = () => {
          const names = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon'];
          return `${Math.floor(Math.random() * 1000)}  ${names[Math.floor(Math.random() * names.length)]}  ${Math.random() < 0.3 ? names[Math.floor(Math.random() * names.length)] : ''}`;
        };

        const getRandomCity = () => {
          const cities = ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille'];
          return cities[Math.floor(Math.random() * cities.length)];
        };

        const getRandomAddress = () => {
          const streets = ['Rue de la Grenouille', 'Avenue des Champs-Élysées', 'Boulevard Saint-Germain', 'Rue de la Paix', 'Place de la Concorde', 'Avenue de la République'];
          return `${Math.floor(Math.random() * 100) + 1} ${streets[Math.floor(Math.random() * streets.length)]}`;
        };

        const getNonLinearSalesAmount = () => {
          const rand = Math.random();
          if (rand < 1 / 3) {
            return 0;
          }
          return parseFloat((Math.sqrt(Math.random()) * 1000000 * selectedYearsValues.reduce((a, b) => a + Number(b), 0)).toFixed(2));
        };

        const firstLettersArray = ['IM', 'CO', 'AS', 'BA', 'AO', 'TE', 'SA', 'SC'];
        const getRandomImmatriculation = () => {
          const firstLetters = firstLettersArray[Math.floor(Math.random() * firstLettersArray.length)];
          return `${firstLetters}${Math.floor(Math.random() * 10000000)}`;
        };

        const randomData = [];
        for (let i = 0; i < coproAmount; i++) {
          const salesAmount = getNonLinearSalesAmount();
          const salesCommission = parseFloat((salesAmount * 0.05).toFixed(2));

          const row = {
            id: window.performance.now(),
            Immatriculation: getRandomImmatriculation(),
            Nom: getRandomName(),
            Adresse: getRandomAddress(),
            'Code postal': Math.floor(10000 + (Math.random() * 90000)).toString(),
            Ville: getRandomCity(),
            'Nb lots hab / local pro': Math.floor(Math.random() * 100 * selectedYearsValues.reduce((a, b) => a + Number(b), 0) / 3) + 1,
            'Nb ventes': Math.floor(Math.random() * 50 * selectedYearsValues.reduce((a, b) => a + Number(b), 0)) + 1,
            'm2 moyen / vente': parseFloat((Math.random() * 500).toFixed(2)),
            'Prix moyen / m2': parseFloat((Math.random() * 10000).toFixed(2)),
            'Montant total des ventes': salesAmount,
            '5% com / ventes': salesCommission,
            Détails: <button className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto">
              Voir le détail
            </button>,
          };
          randomData.push(row);
        }

        randomData.sort((a, b) => b['5% com / ventes'] - a['5% com / ventes']);

        randomData.forEach((element) => {
          Object.keys(element).forEach((key) => {
            if (key === 'Prix moyen / m2' || key === '5% com / ventes' || key === 'Montant total des ventes' || key === 'm2 moyen / vente') {
              let newVal;
              if (key === 'Prix moyen / m2' || key === '5% com / ventes' || key === 'Montant total des ventes') {
                newVal = formatCurrency(Number(element[key]));
              } else {
                newVal = formatValue(element[key]);
              }
              element[key] = newVal;
            }
          });
        });

        setData(randomData as Record<string, string | number | boolean | JSX.Element | undefined | object | unknown[]>[]);
        setStatus('SUCCESS');
      };

      fetch(`/api/companies/${company.id}/stats`, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((response) => {
          const coproAmount = response.nbCopros;
          processResponseData(coproAmount);
        })
        .catch((error) => {
          console.error('Error fetching data :', error);

          // Set a defined fallback value for coproAmount in case of error
          const fallbackCoproAmount = 30;
          processResponseData(fallbackCoproAmount);
        });
      return;
    }

    // Cancel the previous fetch if it exists
    if (currentFetchRef.current) {
      currentFetchRef.current.abort();
    }

    // Create a new AbortController for the current fetch
    const abortController = new AbortController();
    currentFetchRef.current = abortController;

    const params = new URLSearchParams();
    selectedYearsValues.forEach((year) => params.append('years[]', year));

    fetch(`/api/companies/${company.id}/stats/copros/sales/years?${params.toString()}`, {
      headers: {
        Authorization: JWTAuthorization(jwt),
      },
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Internal server error');
        }
        return response.json();
      })
      .then((responseJson) => {
        const dataFetch = responseJson.results || responseJson;
        if (Array.isArray(dataFetch)) {
          dataFetch.sort((a, b) => b.salesCommission - a.salesCommission);
        }
        // Remap data from dataFetchHeaders to finalHeaders
        const finalData = dataFetch.map((row, idx) => {
          const newRow = { id: '' };
          dataFetchHeaders.forEach((header, index) => {
            if (header === 'salesAvgForM2' || header === 'salesCommission' || header === 'salesAmount' || header === 'm2ForNbSales') {
              newRow[finalHeaders[index]] = row[header];
              if (header === 'salesAvgForM2' || header === 'salesCommission' || header === 'salesAmount') {
                newRow[finalHeaders[index]] = formatCurrency(Number(newRow[finalHeaders[index]]));
              } else {
                newRow[finalHeaders[index]] = formatValue(newRow[finalHeaders[index]]);
              }
              return newRow;
            }
            if (header === 'name') {
              newRow[finalHeaders[index]] = (
                <div className="flex items-center">
                  <RedirectionCoproButtons coproId={row.id} />
                  {row[header]}
                </div>
              );
              return newRow;
            }
            if (header === 'address') {
              newRow[finalHeaders[index]] = (
                <div className="flex items-center">
                  <div className="flex h-8 w-8 cursor-pointer items-center justify-center">
                    <MapPinIcon
                      className="mr-2 h-5 w-5 text-gray-600 hover:text-gray-800"
                      onClick={() => {
                        const mapCopros = document.getElementById('map-copros');
                        const mainScrollable = document.getElementById('mainScrollable');
                        if (mapCopros && mainScrollable) {
                          const mapCoprosRect = mapCopros.getBoundingClientRect();
                          const scrollTop = mainScrollable.scrollTop + mapCoprosRect.top - 85;
                          mainScrollable.scrollTo({
                            top: scrollTop,
                            behavior: 'smooth',
                          });
                        }
                        setSelectedLocationIdProp(row.id);
                      }}
                    />
                  </div>
                  {row[header]}
                </div>
              );
              return newRow;
            }
            newRow[finalHeaders[index]] = row[header];
          });
          newRow.id = `${idx}averageSales`;
          newRow['Détails'] = (
            <button
              className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
              onClick={() => handleDetails(row.id, `${row.address} ${row.cp} ${row.city}`)}
            >
              Voir le détail
            </button>
          );
          return newRow;
        });

        // Merge finalData that have the same 'Nb ventes', 'm2 moyen / vente' and 'Prix moyen / m2', 'Montant total des ventes', 'Code postal', 'Ville'. DO not sum the values, concatenate the Immatriculation, Nom and Adresse fields, do not merge if 'Nb ventes' is 0
        const mergedData = Object.values(finalData.reduce((acc, row) => {
          if (row['Nb ventes'] == 0) {
            acc[row.id] = row;
            return acc;
          }
          const key = `${row['Nb ventes']}-${row['m2 moyen / vente']}-${row['Prix moyen / m2']}-${row['Montant total des ventes']}-${row['Code postal']}-${row.Ville}`;
          if (acc[key]) {
            acc[key].Immatriculation = smartConcatMerge(acc[key].Immatriculation, row.Immatriculation);
            acc[key].Nom = smartConcatMerge(acc[key].Nom, row.Nom);
            acc[key].Adresse = smartConcatMerge(acc[key].Adresse, row.Adresse);
            acc[key]['Nb lots hab / local pro'] = smartConcatMerge(acc[key]['Nb lots hab / local pro'], row['Nb lots hab / local pro']);
          } else {
            acc[key] = row;
          }
          return acc;
        }, {}));

        setData(mergedData as Record<string, string | number | boolean | JSX.Element | undefined | object | unknown[]>[]);
        setStatus('SUCCESS');
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
        } else {
          console.error('Error fetching data :', error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatus('ERROR');
        }
      });

    // Cleanup function to abort fetch on unmount
    return () => {
      abortController.abort();
    };
  }, [selectedYears, company, jwt, yearsOptions]);

  return (
    <div className="mb-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="w-full bg-rgpd-blue px-4 py-3.5 text-center">
        <h2 className="text-xs font-semibold tracking-wider text-white uppercase">Moyenne des ventes par copropriété / copropriétés à cibler</h2>
      </div>
      <div className="p-4">
        <div className="mb-2 grid">
          <div className={classNames(company && company.id && !hasModuleAccess(company, 'monitoring') && 'blur-sm pointer-events-none')}>
            <label htmlFor="year-select">Choix des années :</label>
            <SelectMe
              id="year-select"
              name="years"
              value={selectedYears}
              onChange={(selectedOptions) => setSelectedYears(selectedOptions)}
              options={yearsOptions}
              multiple
              closeOnSelect={false}
              listPosition="bottom"
              placeholder="Sélectionnez une ou plusieurs années"
              s={{ dd__selectedItem: '!bg-rgpd-white !border-rgpd-blue-light border-1 mx-2 text-rgpd-blue-light font-semibold' }}
            />
          </div>
        </div>
        {status === 'PENDING' && (
          <div className="w-full pb-2">
            <Table
              data={[]}
              headers={finalHeaders}
              tableClass="w-full"
              columnChecking={false}
              isSearchable
              sortableColumns={sortableColumns}
            />
            <Skeleton count={6} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-2" />
          </div>
        )}
        {status === 'ERROR' && selectedYears.length !== 0
          && (
            <div className="mx-10 flex w-full place-content-center">
              <Badge
                mode="error"
                text="Une erreur est survenue lors du chargement de vos données"
              />
            </div>
          )}
        {status === 'ERROR' && selectedYears.length === 0
          && (
            <div className="flex w-full place-content-center">
              <Badge
                mode="warning"
                text="Veuillez sélectionner au moins une année"
                className="my-10"
              />
            </div>
          )}
        {status === 'SUCCESS' && (
          <>
            {hasModuleAccess(company, 'monitoring') && (
              <>
                <Table
                  data={data}
                  headers={finalHeaders}
                  tableClass=""
                  columnChecking={false}
                  isSearchable
                  sortableColumns={sortableColumns}
                />
                {data.length === 0 && (
                  <div className="flex w-full place-content-center">
                    <Badge
                      mode="warning"
                      text="Aucune copropriété trouvée"
                      className="my-10"
                    />
                  </div>
                )}
                {isPopupOpen && (
                  <Popup
                    isOpen={isPopupOpen}
                    onClose={() => {
                      setCoproSalesDetails(null);
                      setPopupOpen(false);
                    }}
                    title="Détails des ventes"
                  >
                    <div className="mb-4">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:gap-4 lg:grid-cols-5">
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="text-[11px] tracking-wide text-gray-500 uppercase">Prix moy / m2</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {coproSalesDetails
                              ? formatCurrency(Number(kpis.avgPricePerM2))
                              : <Skeleton width={120} height={22} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} />}
                          </div>
                        </div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="text-[11px] tracking-wide text-gray-500 uppercase">Prix moy / vente</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {coproSalesDetails
                              ? formatCurrency(Number(kpis.avgPricePerSale))
                              : <Skeleton width={140} height={22} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} />}
                          </div>
                        </div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="text-[11px] tracking-wide text-gray-500 uppercase">Nombre de ventes</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {coproSalesDetails
                              ? kpis.salesCount
                              : <Skeleton width={60} height={22} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} />}
                          </div>
                        </div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="text-[11px] tracking-wide text-gray-500 uppercase">Montant total des ventes</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {coproSalesDetails
                              ? formatCurrency(Number(kpis.totalSalesAmount))
                              : <Skeleton width={180} height={22} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} />}
                          </div>
                        </div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <div className="text-[11px] tracking-wide text-gray-500 uppercase">Commission totale des ventes</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {coproSalesDetails
                              ? formatCurrency(Number(kpis.totalCommission))
                              : <Skeleton width={200} height={22} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} />}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mb-4 justify-center md:flex md:space-x-4">
                      <RedirectionMapButtons
                        address={selectedCoproAddress}
                        type="standard-inline"
                        buttonClassName="bg-rgpd-blue-dark hover:!bg-rgpd-blue-dark !text-gray-200"
                      />
                    </div>
                    <div className="max-h-[80dvh] min-h-[200px] divide-y divide-dashed overflow-x-scroll">
                      {coproSalesDetails && (
                        <Table
                          data={coproSalesDetails
                            ? coproSalesDetails.map((item, index) => ({
                              id: index,
                              'Valeur Foncière': formatCurrency(Number(item.valeurfonc)),
                              Date: formatDate(item.datemut),
                              'Type Bien': item.libtypbien,
                              'Surface habitale (m²)': item.buildingSurface,
                              'Nombre de Pièces': item.rooms,
                              'Nombre Lots': item.nblot,
                              'Numéros Lots': item.lots,
                            }))
                            : []}
                          headers={['Valeur Foncière', 'Date', 'Type Bien', 'Surface habitale (m²)', 'Nombre de Pièces', 'Nombre Lots', 'Numéros Lots']}
                          tableClass=""
                          columnChecking={false}
                          idKey="id"
                          autoresize
                          tbodyClass=""
                          isSearchable={false}
                          sortableColumns={{
                            'Valeur Foncière': 'number', Date: 'date', Surface: 'number', 'Nombre de Pièces': 'string', 'Nombre Lots': 'number',
                          }}
                        />
                      )}
                      {!coproSalesDetails && (
                        <Skeleton count={6} height={20} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-2" />
                      )}
                    </div>
                  </Popup>
                )}
              </>
            )}
            {!hasModuleAccess(company, 'monitoring') && (
              <div className="relative">
                <div className="absolute inset-0 z-10 flex justify-center bg-gray-100/25">
                  <div className="mt-40 flex flex-col items-center gap-4 text-center">
                    <p className="font-bold text-red-500">Données inaccessibles en version d&apos;essai de l&apos;outil.</p>
                    <Link
                      to={ROUTES.PAYMENT_CHECKOUT}
                      className="group inline-flex max-w-md items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-rgpd-blue to-rgpd-blue-dark px-6 py-3 text-center text-sm leading-snug font-bold text-white shadow-lg transition-all duration-200 hover:scale-[1.03] hover:shadow-xl"
                    >
                      <LockClosedIcon className="h-4 w-4 shrink-0 transition-transform group-hover:-rotate-12" />
                      Débloquer l'accès aux chiffres clés de votre cabinet (vente, location…) globalement et par copropriété depuis 4 ans
                      <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">&rarr;</span>
                    </Link>
                  </div>
                </div>
                <div className="h-full w-full blur-sm">
                  <Table
                    data={data}
                    headers={finalHeaders}
                    columnChecking={false}
                    tableClass=""
                    isSearchable
                    sortableColumns={sortableColumns}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CoproAverageSales;
